'use client';

import { useCallback, useEffect, useState } from 'react';
import Image from 'next/image';

import { Loader2Icon, RotateCcwIcon, StarIcon } from 'lucide-react';

import { Link } from '~/components/common/link';
import { Button } from '~/components/ui/button';
import { cn } from '~/lib/utils';

import { type WordGuessWord } from '../_data/words';
import { getNextWord } from '../_utils/word-deck';
import { GameIntro } from '../../_components/game-intro';
import GameResultDialog from '../../_components/game-result-dialog';

const KEYBOARD_ROWS = [
  ['Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P'],
  ['A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L'],
  ['Z', 'X', 'C', 'V', 'B', 'N', 'M'],
];

type GameStatus = 'playing' | 'won' | 'lost';

const EMPTY_WORD_DATA: WordGuessWord = {
  word: '',
  hint: '',
  image: null,
  partOfSpeech: '',
  definition: '',
};

const getMissedLetterCount = (word: string, guessedLetters: string[]) =>
  word.split('').filter((letter) => !guessedLetters.includes(letter)).length;

const maxWrongGuesses = 6;

const IMAGE_BLUR_BY_WRONG_GUESSES = [16, 12, 8, 4, 2, 0] as const;

export default function WordGuessGame() {
  const [currentWordData, setCurrentWordData] = useState<WordGuessWord>(EMPTY_WORD_DATA);
  const [guessedLetters, setGuessedLetters] = useState<string[]>([]);
  const [wrongGuesses, setWrongGuesses] = useState(0);
  const [gameStatus, setGameStatus] = useState<GameStatus>('playing');
  const [resultsDialogOpen, setResultsDialogOpen] = useState<boolean>(false);
  const [gameId, setGameId] = useState(0);

  const gameIsReady = !!currentWordData.word;

  const startNewGame = useCallback(() => {
    setCurrentWordData(getNextWord());
    setGuessedLetters([]);
    setWrongGuesses(0);
    setGameStatus('playing');
    setGameId((id) => id + 1);
  }, []);

  useEffect(() => {
    startNewGame();
  }, [startNewGame]);

  const guessLetter = useCallback(
    (letter: string) => {
      if (guessedLetters.includes(letter) || gameStatus !== 'playing') return;

      const newGuessedLetters = [...guessedLetters, letter];
      setGuessedLetters(newGuessedLetters);

      if (!currentWordData.word.includes(letter)) {
        const newWrongGuesses = wrongGuesses + 1;
        setWrongGuesses(newWrongGuesses);

        if (newWrongGuesses >= maxWrongGuesses) {
          setGameStatus('lost');
          setResultsDialogOpen(true);
        }
      } else {
        const isComplete = currentWordData.word.split('').every((char) => newGuessedLetters.includes(char));
        if (isComplete) {
          setGameStatus('won');
          setTimeout(() => {
            setResultsDialogOpen(true);
          }, 1500);
        }
      }
    },
    [currentWordData, gameStatus, guessedLetters, wrongGuesses],
  );

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (gameStatus !== 'playing') return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      const key = event.key.toUpperCase();
      if (/^[A-Z]$/.test(key) && !guessedLetters.includes(key)) {
        guessLetter(key);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [gameStatus, guessedLetters, guessLetter]);

  const playAgain = () => {
    setResultsDialogOpen(false);
    startNewGame();
  };

  const renderKeyboard = () => {
    return (
      <div className='sm:bg-blue/5 sm:-mx-(--game-card-padding) sm:-mb-(--game-card-padding) max-sm:-mx-3 max-sm:mt-auto max-sm:rounded-lg sm:p-6'>
        <div className='space-y-2'>
          {KEYBOARD_ROWS.map((row, rowIndex) => (
            <div key={rowIndex} className='flex justify-center gap-2'>
              {row.map((letter) => {
                const isGuessed = guessedLetters.includes(letter);
                const isCorrect = isGuessed && currentWordData.word.includes(letter);
                const isWrong = isGuessed && !currentWordData.word.includes(letter);

                return (
                  <button
                    key={letter}
                    type='button'
                    aria-label={`Guess letter ${letter}`}
                    aria-pressed={isGuessed}
                    onClick={() => guessLetter(letter)}
                    disabled={isGuessed || gameStatus !== 'playing'}
                    className={cn(
                      'aspect-square w-[calc((100%-(.5rem*9))/10)] max-w-9 shrink-0 grow-0 overflow-hidden rounded-sm bg-white shadow shadow-black/15',
                      isGuessed && 'shadow-inner',
                    )}
                  >
                    <div
                      className={cn(
                        'font-greycliff-bold flex h-full items-center justify-center',
                        isCorrect && 'bg-teal/20 text-teal',
                        isWrong && 'bg-magenta/15 text-magenta',
                      )}
                    >
                      {letter}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const imageBlur =
    gameStatus === 'playing'
      ? IMAGE_BLUR_BY_WRONG_GUESSES[Math.min(wrongGuesses, IMAGE_BLUR_BY_WRONG_GUESSES.length - 1)]
      : 0;

  const gameWon = gameStatus === 'won';
  const missedLetterCount = getMissedLetterCount(currentWordData.word, guessedLetters);

  return (
    <>
      <div
        className={cn(
          'transition-opacity duration-500 ease-out max-sm:flex max-sm:h-full max-sm:flex-col',
          !gameIsReady ? 'opacity-0' : 'opacity-100',
        )}
      >
        <GameIntro title='Can you guess the hidden word?' description={`Hint: ${currentWordData.hint}`} />
        <div key={gameId} className='relative mx-auto aspect-video w-full max-w-80 overflow-hidden rounded-lg'>
          {currentWordData.image && (
            <Image
              className='aspect-video object-cover'
              src={currentWordData.image}
              alt='Word Guess Image'
              width={320}
              height={180}
            />
          )}
          <div
            className='absolute inset-0 rounded-lg transition-[backdrop-filter] duration-500 ease-out'
            style={{ backdropFilter: `blur(${imageBlur}px)`, backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          />
        </div>

        <div className='mt-8 flex justify-center gap-x-2'>
          {currentWordData.word.split('').map((letter, index) => {
            const isGuessed = guessedLetters.includes(letter);

            return (
              <span
                key={index}
                className={cn(
                  'border-asphalt-10 bg-asphalt-10/25 font-greycliff-extrabold max-w-10 flex-1 grow border-b-2 pb-1 pt-2 text-center text-xl md:text-2xl',
                  isGuessed && 'repeat-1 text-teal border-teal bg-teal/10 animate-pulse',
                )}
              >
                {isGuessed ? letter : <>&nbsp;</>}
              </span>
            );
          })}
          {!gameIsReady && <span className='border-b-2 pb-1 pt-2 text-xl md:text-2xl'>&nbsp;</span>}
        </div>

        <div className='text-asphalt-75 my-5 flex items-center justify-center gap-x-3 text-sm'>
          <span>Incorrect guesses remaining:</span>
          <div className='flex flex-row-reverse gap-x-1'>
            {Array.from({ length: maxWrongGuesses }).map((_, index) => (
              <StarIcon
                key={index}
                strokeWidth={0}
                className={cn('size-5', wrongGuesses > index ? ' fill-asphalt-10' : 'fill-yellow')}
              />
            ))}
          </div>
        </div>
        {renderKeyboard()}
      </div>

      {/* Loader */}
      {!gameIsReady && (
        <div className='absolute inset-0 z-50 flex items-center justify-center bg-white'>
          <Loader2Icon className='text-blue size-6 animate-spin' />
        </div>
      )}

      {/* Results Modal */}
      <GameResultDialog
        title={gameWon ? 'Great Job!' : 'Nice Try!'}
        open={resultsDialogOpen}
        setOpen={setResultsDialogOpen}
        showConfetti={resultsDialogOpen && gameWon}
        result={gameWon ? 'won' : 'lost'}
        description={
          gameWon ? undefined : `You were only ${missedLetterCount} letter${missedLetterCount !== 1 ? 's' : ''} off.`
        }
      >
        <div className='text-asphalt-75 mx-auto mb-5 flex items-center justify-center gap-x-3 text-sm'>
          <hr className='border-asphalt-10 w-5' />
          {gameWon ? 'You guessed the hidden word' : 'The hidden word was'}
          <hr className='border-asphalt-10 w-5' />
        </div>

        <div className='flex justify-center gap-x-1'>
          {currentWordData.word.split('').map((letter, index) => {
            const wasGuessed = guessedLetters.includes(letter);

            return (
              <div
                className={cn(
                  'font-greycliff-extrabold max-w-10 grow border-b-2 pb-1 pt-2 text-center text-xl md:text-2xl',
                  wasGuessed ? 'bg-teal/10 border-teal text-teal' : 'bg-magenta/15 border-magenta text-magenta',
                )}
                key={index}
              >
                {letter}
              </div>
            );
          })}
        </div>

        <div className='text-asphalt-75 mb-6 mt-3 text-balance px-2 text-center text-sm'>
          <p>
            <em>{currentWordData.partOfSpeech}:</em> {currentWordData.definition}
          </p>
          {currentWordData.secondaryDefinition && (
            <p className='mt-1'>
              <em>Also:</em> {currentWordData.secondaryDefinition}
            </p>
          )}
        </div>

        <div className='flex flex-col gap-y-3'>
          <Button onClick={playAgain}>
            <RotateCcwIcon /> Play Again
          </Button>

          <Button variant='outline-secondary' asChild>
            <Link href='/games'>More Games</Link>
          </Button>
        </div>
      </GameResultDialog>
    </>
  );
}
